/**
 * Environment detection utilities for Vercel deployments
 */

/**
 * Determines if the current environment is a Vercel preview environment
 * @returns boolean indicating if running in a preview environment
 */
export function isPreviewEnvironment(): boolean {
  // Check Vercel's built-in environment variable first (most reliable)
  if (typeof process !== 'undefined' && process.env.VERCEL_ENV === 'preview') {
    return true;
  }
  
  // Check hostname as fallback for client-side detection
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    // Vercel preview deployments use unique URLs like project-name-git-branch-username.vercel.app
    return hostname.includes('vercel.app') && !hostname.includes('mixus.ai');
  }
  
  return false;
}

/**
 * Determines if the current environment is production
 * @returns boolean indicating if running in production
 */
export function isProductionEnvironment(): boolean {
  // Check Vercel's built-in environment variable first
  if (typeof process !== 'undefined' && process.env.VERCEL_ENV === 'production') {
    return true;
  }
  
  // Check hostname as fallback
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    // Main domain without 'dev' subdomain is production
    return hostname.includes('mixus.ai') && !hostname.includes('dev.mixus.ai');
  }
  
  return false;
}

/**
 * Determines if the current environment is a development environment (local or dev server)
 * @returns boolean indicating if running in development
 */
export function isDevelopmentEnvironment(): boolean {
  // Check Vercel's built-in environment variable first
  if (typeof process !== 'undefined') {
    // Both 'development' value and NODE_ENV are reliable indicators
    if (process.env.VERCEL_ENV === 'development' || process.env.NODE_ENV === 'development') {
      return true;
    }
  }
  
  // Check hostname as fallback
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    return hostname === 'localhost' || 
           hostname === '127.0.0.1' || 
           hostname.includes('dev.mixus.ai');
  }
  
  return false;
}

/**
 * Gets the appropriate database name based on the current environment
 * @returns string with the database name to use
 */
export function getEnvironmentDatabaseName(): string {
  // Get database names from environment variables
  const devDbName = process.env.MONGODB_DB_DEV || 'geniiusdb04-dev';
  const prodDbName = process.env.MONGODB_DB_PROD || process.env.MONGODB_DB || 'geniiusdb04-prod';
  
  // Check Vercel environment variable first - this is reliable when available
  if (typeof process !== 'undefined' && process.env.VERCEL_ENV) {
    if (process.env.VERCEL_ENV === 'preview' || process.env.VERCEL_ENV === 'development') {
      return devDbName;
    } else if (process.env.VERCEL_ENV === 'production') {
      return prodDbName;
    }
  }
  
  // Client-side detection as fallback
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    
    // Localhost or development domain
    if (hostname === 'localhost' || 
        hostname === '127.0.0.1' || 
        hostname.includes('dev.mixus.ai')) {
      return devDbName;
    }
    
    // Vercel preview deployments have URLs like project-name-git-branch-username.vercel.app
    if (hostname.includes('vercel.app')) {
      return devDbName;
    }
    
    // Production domain
    if (hostname.includes('mixus.ai') && !hostname.includes('dev.mixus.ai')) {
      return prodDbName;
    }
  }
  
  // Default fallback - safest to use dev in ambiguous cases
  console.warn('[Environment] Could not definitively determine environment, defaulting to dev database');
  return devDbName;
} 